// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  CapitalCallRecord,
  validateCapitalCallRecord
} from '../../../state_manager/data_models/capital_call_record.ts'
import {
  CapitalCallDetail,
  CapitalCallSavePayload,
  FundContributionRow,
  FundPaymentStatusRow,
  validateCapitalCallDetail,
  validateFundContributionRow,
  validateFundContributionRows,
  validateFundPaymentStatusRows
} from '../../../state_manager/data_models/capital_call_detail.ts'
import {
  CapitalCallNoticeData,
  validateCapitalCallNoticeData
} from '../../../state_manager/data_models/capital_call_notice_data.ts'
import {
  CapitalCallPosition,
  validateCapitalCallPosition
} from '../../../state_manager/data_models/capital_call_position.ts'
import {
  EqualizationPreview,
  validateEqualizationPreview
} from '../../../state_manager/data_models/capital_call_equalization.ts'
import { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface CapitalCallsPaginatedResponse {
  data: CapitalCallRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface CapitalCallDetailResponse {
  data: CapitalCallDetail | null
  error?: string
}

export interface CapitalCallActionResponse {
  data: Record<string, any> | null
  error?: string
  /** true when the server returned the duplicate_warning envelope (409). */
  duplicateWarning?: boolean
  /** true when the server rejected individual rows (422); see data.errors. */
  validationError?: boolean
}

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export const fetchCapitalCallsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: { fundId?: string | null }
): Promise<CapitalCallsPaginatedResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value })),
        ...(options?.fundId ? { fund_id: options.fundId } : {})
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.FILTER), {
      method: API_ROUTES.CAPITAL_CALLS.FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch capital calls: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? json.data : []
    return {
      data: validateCapitalCallRecord(rows, true),
      hasMore: Boolean(json.has_more),
      totalCount: json.count ?? rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching capital calls:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchCapitalCallsFilterOptions = async (
  accessToken: string | null = null
): Promise<{ data: Record<string, Array<{ value: string; label: string }>>; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.FILTER_OPTIONS), {
      method: API_ROUTES.CAPITAL_CALLS.FILTER_OPTIONS.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching capital call filter options:', error)
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchCapitalCallDetail = async (
  capitalCallId: string,
  accessToken: string | null = null
): Promise<CapitalCallDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.GET, { id: capitalCallId }, {})
    const response = await fetch(url, { method: API_ROUTES.CAPITAL_CALLS.GET.method, headers: authHeaders(accessToken) })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      if (response.status === 404) throw new Error('Capital call not found')
      throw new Error(`Failed to fetch capital call: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateCapitalCallDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching capital call detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * What the notices of a call are built from (cc__notice_data): the notice
 * period given against the one the fund's LPA requires, the problems that
 * stop a notice being produced, and one row per LP. Works for a Draft call.
 */
export const fetchCapitalCallNoticeData = async (
  capitalCallId: string,
  accessToken: string | null = null
): Promise<{ data: CapitalCallNoticeData | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.NOTICE_DATA, { id: capitalCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.NOTICE_DATA.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      throw new Error(body.message || `Failed to fetch notice data: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateCapitalCallNoticeData(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching capital call notice data:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Create or update a Draft capital call. The backend returns the full
 * refreshed detail (call + items + per-commitment + transaction) in the same
 * round-trip.
 */
export const saveCapitalCall = async (
  payload: CapitalCallSavePayload,
  accessToken: string | null = null
): Promise<CapitalCallDetailResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.SAVE), {
      method: API_ROUTES.CAPITAL_CALLS.SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })

    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.message || json.error || `Failed to save capital call: ${response.status}`)
    }

    if (!json.data) return { data: null }
    return { data: validateCapitalCallDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving capital call:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

type CapitalCallActionRoute =
  | typeof API_ROUTES.CAPITAL_CALLS.COMPUTE
  | typeof API_ROUTES.CAPITAL_CALLS.CLEAR_OVERRIDES
  | typeof API_ROUTES.CAPITAL_CALLS.ISSUE
  | typeof API_ROUTES.CAPITAL_CALLS.REVERT

/**
 * Lifecycle actions (compute / clear-overrides / issue / revert) return the refreshed detail on
 * success; server-side state rejections surface as an error with the RPC
 * envelope attached.
 */
async function postCallAction(
  route: CapitalCallActionRoute,
  capitalCallId: string,
  accessToken: string | null
): Promise<CapitalCallDetailResponse> {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, route, { id: capitalCallId }, {})
    const response = await fetch(url, { method: route.method, headers: authHeaders(accessToken) })
    const json = (await response.json().catch(() => ({}))) as {
      data?: unknown
      message?: string
      error?: string
    }

    if (!response.ok) {
      const message = json.error || json.message || `Request failed: ${response.status}`
      return { data: null, error: message }
    }

    if (!json.data) return { data: null }
    return { data: validateCapitalCallDetail(json.data) }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const computeCapitalCall = (capitalCallId: string, accessToken: string | null = null) =>
  postCallAction(API_ROUTES.CAPITAL_CALLS.COMPUTE, capitalCallId, accessToken)

export const clearCapitalCallOverrides = (capitalCallId: string, accessToken: string | null = null) =>
  postCallAction(API_ROUTES.CAPITAL_CALLS.CLEAR_OVERRIDES, capitalCallId, accessToken)

export const issueCapitalCall = (capitalCallId: string, accessToken: string | null = null) =>
  postCallAction(API_ROUTES.CAPITAL_CALLS.ISSUE, capitalCallId, accessToken)

export const revertCapitalCall = (capitalCallId: string, accessToken: string | null = null) =>
  postCallAction(API_ROUTES.CAPITAL_CALLS.REVERT, capitalCallId, accessToken)

/**
 * Rename a call in any status. On an Issued call this is the only header
 * edit: the call, its consolidated header and the booked descriptions that
 * carried the old name follow server-side. Returns the refreshed detail.
 */
export const renameCapitalCall = async (
  capitalCallId: string,
  name: string,
  accessToken: string | null = null
): Promise<CapitalCallDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.RENAME, { id: capitalCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.RENAME.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { name } })
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.message || json.error || `Failed to rename capital call: ${response.status}`)
    }

    if (!json.data) return { data: null }
    return { data: validateCapitalCallDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error renaming capital call:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const trashCapitalCall = async (
  capitalCallId: string,
  accessToken: string | null = null
): Promise<CapitalCallActionResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.DELETE, { id: capitalCallId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.DELETE.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return { data: json.data ?? null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// -- fund-tab reads ---------------------------------------------------------

export const fetchFundPaymentStatus = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: FundPaymentStatusRow[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.PAYMENT_STATUS, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.PAYMENT_STATUS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch payment status: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateFundPaymentStatusRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching payment status:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundContributions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: FundContributionRow[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.CONTRIBUTIONS, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.CONTRIBUTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch contributions: ${response.status}`)
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateFundContributionRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching contributions:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface BatchPaymentRow {
  fund_commitment_id: string
  amount: number
  /** omitted when the row inherits the batch date */
  payment_date?: string
}

export interface RecordContributionsBatchPayload {
  fund_id: string
  /** the batch date: the fallback for any row without its own */
  payment_date: string
  rows: BatchPaymentRow[]
  /** resend with true after the user confirms the duplicate warning */
  confirm_duplicate?: boolean
}

/**
 * Record N LP payments in one all-or-nothing transaction. The server writes
 * nothing when it returns either the 409 duplicate envelope or the 422
 * row-validation envelope, so both are safe to confirm or correct and resend.
 */
export const recordContributionsBatch = async (
  payload: RecordContributionsBatchPayload,
  accessToken: string | null = null
): Promise<CapitalCallActionResponse> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.RECORD_CONTRIBUTIONS_BATCH),
      {
        method: API_ROUTES.CAPITAL_CALLS.RECORD_CONTRIBUTIONS_BATCH.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: payload })
      }
    )
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return {
        data: json.data ?? null,
        error: json.error || json.message || `Request failed: ${response.status}`,
        duplicateWarning: response.status === 409 && json.data?.status === 'duplicate_warning',
        validationError: response.status === 422 && json.data?.status === 'error'
      }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface CommitmentOption {
  value: string
  label: string
}

/**
 * {value, label} options: the fund's live Closed commitments. Used by the
 * payment editor when an investor is corrected - a payment can only move
 * between commitments in the SAME fund, which is why this is fund-scoped.
 */
export const fetchFundCommitmentOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: CommitmentOption[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.COMMITMENT_OPTIONS, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.COMMITMENT_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch commitment options: ${response.status}`)
    const json = (await response.json()) as { data?: CommitmentOption[] }
    return { data: json.data ?? [] }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching commitment options:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export interface UpdateContributionPayload {
  amount: number
  payment_date: string
  /** only sent when the investor was actually changed; same fund only */
  fund_commitment_id?: string
  /** resend with true after the user confirms the duplicate warning */
  confirm_duplicate?: boolean
}

/**
 * One contribution in the fund-list row shape. The payment editor opens from
 * the fund tab and from a Capital Call Receipt fund transaction, which knows
 * only the contribution id - so it reads by id rather than pulling a whole
 * fund's payments, and re-reads rather than trusting a possibly stale row.
 */
export const fetchContributionDetail = async (
  contributionId: string,
  accessToken: string | null = null
): Promise<{ data: FundContributionRow | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.CONTRIBUTION_DETAIL, { id: contributionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.CONTRIBUTION_DETAIL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch contribution: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: json.data ? validateFundContributionRow(json.data) : null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching contribution:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Edit a booked payment in place. The server unbooks the receipt, rewrites it
 * and books it again in one transaction, keeping the contribution and the
 * transaction ids - so this is a correction, not a delete and re-record.
 * A 409 duplicate envelope writes nothing and is safe to confirm through.
 */
export const updateContribution = async (
  contributionId: string,
  payload: UpdateContributionPayload,
  accessToken: string | null = null
): Promise<CapitalCallActionResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.UPDATE_CONTRIBUTION, { id: contributionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.UPDATE_CONTRIBUTION.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return {
        data: json.data ?? null,
        error: json.error || json.message || `Request failed: ${response.status}`,
        duplicateWarning: response.status === 409 && json.data?.status === 'duplicate_warning'
      }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const deleteContribution = async (
  contributionId: string,
  accessToken: string | null = null
): Promise<CapitalCallActionResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.DELETE_CONTRIBUTION, { id: contributionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.DELETE_CONTRIBUTION.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return { data: json.data ?? null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundCallPosition = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: CapitalCallPosition | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.POSITION, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.POSITION.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch fund position: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateCapitalCallPosition(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund position:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundEqualizationPreview = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: EqualizationPreview | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.EQUALIZATION_PREVIEW, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.EQUALIZATION_PREVIEW.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch equalization preview: ${response.status}`)
    // the RPC owns the envelope (data + policy + totals) and the backend
    // passes it through at the top level rather than under `data`
    const json = await response.json()
    return { data: validateEqualizationPreview(json) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching equalization preview:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const createEqualizationCall = async (
  fundId: string,
  rateTopup: boolean = true,
  /** ISO YYYY-MM-DD; null = today (server default) */
  callDate: string | null = null,
  accessToken: string | null = null
): Promise<{ data: CapitalCallDetail | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CAPITAL_CALLS.EQUALIZATION_CALL, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.CAPITAL_CALLS.EQUALIZATION_CALL.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ rate_topup: rateTopup, call_date: callDate })
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: unknown
      error?: string
      message?: string
    }
    if (!response.ok) {
      // 422 = user-fixable state (policy not set / nothing outstanding)
      return { data: null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { data: validateCapitalCallDetail(json.data ?? {}) }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
